'use client';

/**
 * Benachrichtigungs-Glocke (Admin-Topbar). Reiner Consumer von
 * NotificationsProvider — kein eigener Fetch/Channel (Provider hält State +
 * EINE Realtime-Subscription, geteilt mit Mobile/Desktop-Glocke + Vollseite).
 * Unread-Badge, Dropdown, mark-read on open, Klick → Ressource.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  Bell,
  CalendarPlus,
  CalendarClock,
  CalendarX,
  Gift,
  Ticket,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { useAdminFormatters } from '@/hooks/useAdminFormatters';
import { useNotifications, type AppNotification } from './NotificationsProvider';

const ICON: Record<string, LucideIcon> = {
  new_booking: CalendarPlus,
  new_request: CalendarClock,
  booking_cancelled: CalendarX,
  gift_card_purchased: Gift,
  package_purchased: Ticket,
};

const LINK: Record<string, string> = {
  booking: '/admin/bookings',
  gift_card: '/admin/gift-cards',
  service_package: '/admin/packages',
};

export function NotificationBell() {
  const t = useTranslations('admin.notifications');
  const fmt = useAdminFormatters();
  const router = useRouter();
  const { items, unreadCount, markRead, markAllRead } = useNotifications();

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Klick außerhalb schließt das Panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const relativeTime = useMemo(() => {
    const rtf = new Intl.RelativeTimeFormat(fmt.ctx.locale, { numeric: 'auto' });
    return (iso: string) => {
      const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
      if (Math.abs(mins) < 60) return rtf.format(mins, 'minute');
      const hours = Math.round(mins / 60);
      if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
      return rtf.format(Math.round(hours / 24), 'day');
    };
  }, [fmt.ctx.locale]);

  const openItem = useCallback(
    (n: AppNotification) => {
      setOpen(false);
      if (!n.read) markRead(n.id);
      const href = n.related_resource_type ? LINK[n.related_resource_type] : null;
      if (href) router.push(href);
    },
    [markRead, router],
  );

  const label = (type: string) => {
    const key = `types.${type}`;
    const v = t(key);
    return v === key ? type : v;
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('title')}
        aria-haspopup="true"
        aria-expanded={open}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
      >
        <Bell className="h-5 w-5" strokeWidth={2} />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-[18px] text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[min(92vw,22rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <span className="text-sm font-semibold text-gray-950">{t('title')}</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                <Check className="h-3.5 w-3.5" />
                {t('markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-gray-400">{t('empty')}</p>
            ) : (
              items.map((n) => {
                const Icon = ICON[n.type] ?? Bell;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openItem(n)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                      n.read ? '' : 'bg-blue-50/40'
                    }`}
                  >
                    <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                      <Icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-gray-950">{label(n.type)}</span>
                        {!n.read && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />}
                      </span>
                      {n.message && <span className="mt-0.5 block truncate text-xs text-gray-500">{n.message}</span>}
                      <span className="mt-0.5 block text-[11px] text-gray-400">{relativeTime(n.created_at)}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              router.push('/admin/notifications');
            }}
            className="block w-full border-t border-gray-100 px-4 py-2.5 text-center text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            {t('viewAll')}
          </button>
        </div>
      )}
    </div>
  );
}
